import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { keys as keysApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Key, Plus, Copy, Trash2, AlertTriangle, Loader2 } from 'lucide-react';

export default function ApiKeys() {
  const { user } = useAuth();
  const [keysList, setKeysList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadKeys = async () => {
    try {
      const res = await keysApi.list();
      setKeysList(res.data.keys || []);
    } catch {}
  };

  useEffect(() => { loadKeys(); }, []);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const res = await keysApi.create({ name: newKeyName || 'Default' });
      setCreatedKey(res.data);
      setShowCreate(false);
      setNewKeyName('');
      loadKeys();
      toast.success('API key created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    }
    setLoading(false);
  };

  const handleRevoke = async (keyId) => {
    try {
      await keysApi.revoke(keyId);
      loadKeys();
      toast.success('Key revoked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('Copied to clipboard');
  };

  return (
    <div data-testid="api-keys-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">API Keys</h2>
          <p className="text-muted-foreground mt-1">Manage your API keys for accessing services</p>
        </div>
        <Button data-testid="create-key-btn" onClick={() => setShowCreate(true)} className="bg-primary hover:bg-primary/90 shadow-[0_0_10px_rgba(99,102,241,0.3)]">
          <Plus className="w-4 h-4 mr-2" />
          Create Key
        </Button>
      </div>

      {createdKey && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-amber-400">Save your API key now - it won't be shown again!</p>
                <div className="flex items-center gap-2">
                  <code data-testid="new-key-value" className="flex-1 p-2 rounded bg-background border border-border text-xs font-mono break-all">{createdKey.key}</code>
                  <Button size="sm" variant="outline" onClick={() => copyKey(createdKey.key)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setCreatedKey(null)} className="text-xs">Dismiss</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" />
            Your Keys
          </CardTitle>
        </CardHeader>
        <CardContent>
          {keysList.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No API keys yet. Create one to get started.</p>
          ) : (
            <div className="space-y-2">
              {keysList.map(k => (
                <div key={k.key_id} data-testid={`key-row-${k.key_id}`} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/20">
                  <div className="flex items-center gap-3">
                    <code className="text-xs font-mono text-muted-foreground">{k.key_prefix}...</code>
                    <span className="text-sm font-medium">{k.name}</span>
                    <Badge variant={k.is_active ? 'default' : 'secondary'} className={k.is_active ? 'bg-emerald-500/20 text-emerald-400' : ''}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    {k.last_used_at && <span className="text-xs text-muted-foreground">Last used: {new Date(k.last_used_at).toLocaleDateString()}</span>}
                    {k.is_active && (
                      <Button data-testid={`revoke-key-${k.key_id}`} size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleRevoke(k.key_id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Give your key a name to identify it later</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="key-name-input" placeholder="e.g., Production, Development" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button data-testid="confirm-create-key" onClick={handleCreate} disabled={loading} className="bg-primary hover:bg-primary/90">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
